import React, { useContext } from 'react';
import { useTitle } from 'react-use';

import { BUTTON_TYPES } from '@savgroup-front-common/constants';
import { CommonWorkflowService } from '@savgroup-front-common/core/api';
import { Button } from '@savgroup-front-common/core/atoms/button';
import { Form } from '@savgroup-front-common/core/atoms/Form';
import { AdditionalInformation } from '@savgroup-front-common/core/components/AdditionalInformationWithReactHookForm';
import { SafeFormattedMessageWithoutSpread } from '@savgroup-front-common/core/formatters';
import {
  buildPageTitle,
  SUPPORTED_ICON_POSITIONS,
} from '@savgroup-front-common/core/helpers';
import { useUploadAttachementsOnClaimGroup } from '@savgroup-front-common/core/hooks/useUploadAttachementsOnClaimGroup';
import { BaseLoader } from '@savgroup-front-common/core/molecules/BaseLoader';
import { useRoutedStepsOrchestratorContext } from '@savgroup-front-common/core/molecules/RoutedStepsOrchestrator/RoutedStepsOrchestrator.context';
import { RoutedStepProps } from '@savgroup-front-common/core/molecules/RoutedStepsOrchestrator/RoutedStepsOrchestrator.types';
import ArrowNakedLeftIcon from '@savgroup-front-common/core/protons/icons/ArrowNaked/ArrowNakedLeft.icon';
import { ArrowNakedRightIcon } from '@savgroup-front-common/core/protons/icons/ArrowNaked/ArrowNakedRight.icon';

import useGetSellerConfiguration from '../../../app/hooks/useGetSellerConfiguration';
import { STEP_CONFIG } from '../../../app/NewLayout/ClassiqueRoutes.config';
import { InitContext } from '../../../app/NewLayout/InitProvider/InitProvider.context';
import {
  NewStepLayout,
  PageFooterContainer,
} from '../../../app/NewLayout/NewStepLayout';
import { IrshStepValues } from '../IrshPages.types';

import messages from './messages';
import useClaimGroupProductInfo from './NewClaimGroupProductInfoPage.hooks';
import ProductHeader from './ProductHeader/ProductHeader';

export const NewClaimGroupProductInfoPage: React.FC<
  RoutedStepProps<IrshStepValues>
> = ({ onNextStep, onPreviousStep }) => {
  const { orders } = useContext(InitContext);
  const sellerId = orders ? orders[0].sellerId : undefined;

  useTitle(buildPageTitle([messages.claimGroupProductInfoTitlePage]));

  const {
    claims,
    products,
    neededInformationOnlyRelatedToProduct,
    onSubmit,
    formContext,
    isLoadingSubmit,
  } = useClaimGroupProductInfo({
    onNextStep,
  });

  const { values } = useRoutedStepsOrchestratorContext<IrshStepValues>();
  const claimGroupId = values?.claimGroupId;

  const { handleUploadAttachementOnClaimGroup } =
    useUploadAttachementsOnClaimGroup({
      claimGroupId,
      claimIds: values?.claims?.map((claim) => claim.claimId) ?? [],
    });

  const { sellerConfiguration: seller } = useGetSellerConfiguration({
    sellerId: values.order?.sellerId,
  });

  if (!sellerId) {
    return <BaseLoader />;
  }

  return (
    <Form formContext={formContext} onSubmit={onSubmit}>
      <NewStepLayout stepName={STEP_CONFIG.CLAIM_GROUP_PRODUCT_INFO}>
        {claims?.map((claim) => {
          const neededInformationOnlyRelatedToProductFiltered =
            neededInformationOnlyRelatedToProduct?.filter(
              (item) =>
                item.currentClaimId === claim.claimId &&
                item.claimIds.includes(claim.claimId),
            );

          return (
            <div key={claim.claimId}>
              {products && products[claim.ownerProductId] && (
                <ProductHeader product={products[claim.ownerProductId]} />
              )}
              <AdditionalInformation
                neededInformation={
                  neededInformationOnlyRelatedToProductFiltered
                }
                formContext={formContext}
                prefixFormName={`${claim.claimId}.reasonAdditionalInformationProductInfo`}
                ignoreUniquenessByProduct
                fullSizeItem
                isScrollIntoView
                isNewDesign
                onNeedToUploadFileAttachements={
                  handleUploadAttachementOnClaimGroup
                }
                sellerId={sellerId}
                getQrUploadLink={
                  CommonWorkflowService.getFileAttachmentUploadLinkFromMyAccountCommand
                }
                currency={seller?.currency}
              />
            </div>
          );
        })}
      </NewStepLayout>

      <PageFooterContainer>
        <Button
          primary
          type={BUTTON_TYPES.BUTTON}
          disabled={isLoadingSubmit}
          onClick={onSubmit}
          position={SUPPORTED_ICON_POSITIONS.RIGHT}
          icon={<ArrowNakedRightIcon />}
          dataTestId="nextButton"
        >
          <SafeFormattedMessageWithoutSpread message={messages.nextButton} />
        </Button>

        <Button
          type={BUTTON_TYPES.BUTTON}
          tertiary
          onClick={() => {
            onPreviousStep({});
          }}
          icon={<ArrowNakedLeftIcon />}
          position={SUPPORTED_ICON_POSITIONS.LEFT}
          dataTestId="previousButton"
        >
          <SafeFormattedMessageWithoutSpread
            message={messages.previousButton}
          />
        </Button>
      </PageFooterContainer>
    </Form>
  );
};
